class GutMasterQuiz extends HTMLElement {
  connectedCallback() {
    this.noChromeClass = "gm-quiz--no-chrome";
    this.toggleChrome(true);
    this.steps = Array.from(this.querySelectorAll("[data-quiz-step]"));
    this.progressBar = this.querySelector("[data-quiz-progress-bar]");
    this.progressLabel = this.querySelector("[data-quiz-progress-label]");
    this.headerEl = this.querySelector(".gm-quiz__header");
    this.klaviyoKey = (this.dataset.klaviyoKey || "").trim();
    this.klaviyoTag = (this.dataset.klaviyoTag || "").trim();
    this.klaviyoEventName = (this.dataset.klaviyoEvent || "").trim() || "Gut Master Quiz Completed";
    this.symptomLimit = Number.parseInt(this.dataset.symptomLimit || "3", 10);
    this.probioticYesValues = this.parseList(this.dataset.probioticYesValues);
    this.gummyValue = (this.dataset.gummyValue || "").trim();
    this.fallbackBenefits = this.parseFallbackBenefits();
    this.history = [];
    this.answers = {};
    this.currentStepId = null;
    this.planData = this.parsePlanData();

    this.bindEvents();
    this.setHeaderHeight();

    if (this.headerEl) {
      this.headerEl.querySelectorAll("img").forEach((img) => {
        if (!img.complete) {
          img.addEventListener("load", () => this.setHeaderHeight(), { once: true });
        }
      });
    }

    this.showStep("1");
  }

  disconnectedCallback() {
    if (this.handleResize) {
      window.removeEventListener("resize", this.handleResize);
    }
    this.toggleChrome(false);
  }

  toggleChrome(enabled) {
    if (!document) {
      return;
    }

    if (enabled) {
      document.documentElement.classList.add(this.noChromeClass);
      if (document.body) {
        document.body.classList.add(this.noChromeClass);
      }
      return;
    }

    if (!document.querySelector("gut-master-quiz")) {
      document.documentElement.classList.remove(this.noChromeClass);
      if (document.body) {
        document.body.classList.remove(this.noChromeClass);
      }
    }
  }

  setHeaderHeight() {
    if (!this.headerEl) {
      return;
    }

    const height = this.headerEl.offsetHeight;
    this.style.setProperty("--gm-quiz-header-height", `${height}px`);

    if (!this.handleResize) {
      this.handleResize = () => this.setHeaderHeight();
      window.addEventListener("resize", this.handleResize);
    }
  }

  parseList(value) {
    if (!value) {
      return [];
    }

    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  parseFallbackBenefits() {
    const raw = this.dataset.fallbackBenefits || "";
    return raw
      .split("||")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  parsePlanData() {
    const planDataEl = this.querySelector("[data-quiz-plan-data]");
    if (!planDataEl) {
      return null;
    }

    try {
      return JSON.parse(planDataEl.textContent);
    } catch (error) {
      return null;
    }
  }

  bindEvents() {
    this.addEventListener("change", (event) => this.handleChange(event));
    this.addEventListener("click", (event) => this.handleClick(event));

    const form = this.querySelector("form[data-quiz-form]");
    if (form) {
      form.addEventListener("submit", (event) => this.handleSubmit(event, form));
    }
  }

  handleChange(event) {
    const input = event.target;

    if (input.matches("[data-quiz-input]")) {
      const stepEl = input.closest("[data-quiz-step]");
      if (!stepEl) {
        return;
      }

      this.collectAnswers(stepEl);
      this.updateContinueState(stepEl);

      if (stepEl.dataset.autoAdvance === "true" && input.checked) {
        this.goNext();
      }

      return;
    }

    if (input.matches("[data-quiz-subscription]")) {
      const price = input.dataset.price || "";
      this.updateOfferPrice(price);
      this.syncOfferForm();
      return;
    }

    if (input.matches("[data-quiz-variant-select]")) {
      this.updateVariantSelection(input);
      return;
    }

    if (input.matches("[data-quiz-plan-select]")) {
      this.updatePlanSelection(input);
      return;
    }
  }

  handleClick(event) {
    const redirectButton = event.target.closest("[data-quiz-redirect]");
    if (redirectButton) {
      event.preventDefault();
      const stepEl = redirectButton.closest("[data-quiz-step]");
      const emailInput = stepEl ? stepEl.querySelector("input[type='email']") : null;
      if (emailInput && !emailInput.checkValidity()) {
        emailInput.reportValidity();
        return;
      }
      const email = emailInput ? emailInput.value.trim() : "";
      const redirectUrl = redirectButton.dataset.quizRedirect;
      const sendPromise = email ? this.sendKlaviyo(email) : Promise.resolve();
      if (redirectUrl) {
        Promise.race([
          sendPromise.catch(() => null),
          new Promise((resolve) => setTimeout(resolve, 600)),
        ]).finally(() => {
          window.location.href = redirectUrl;
        });
      }
      return;
    }

    const continueButton = event.target.closest("[data-quiz-continue]");
    if (continueButton) {
      event.preventDefault();
      this.goNext();
      return;
    }

    const backButton = event.target.closest("[data-quiz-back]");
    if (backButton) {
      event.preventDefault();
      this.goBack();
    }
  }

  handleSubmit(event, form) {
    event.preventDefault();
    event.stopPropagation();

    if (form.dataset.submitting === "true") {
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    form.dataset.submitting = "true";

    const formData = new FormData(form);

    fetch(form.action, {
      method: "POST",
      body: formData,
      headers: {
        Accept: "text/html",
      },
    })
      .then(() => {
        this.showStep("offer");
        this.history = [];
      })
      .catch(() => {
        this.showFormMessage(form, "Something went wrong. Please try again.");
      })
      .finally(() => {
        form.dataset.submitting = "false";
      });
  }

  showFormMessage(form, message) {
    let messageEl = form.querySelector("[data-quiz-form-message]");

    if (!messageEl) {
      messageEl = document.createElement("p");
      messageEl.className = "gm-quiz__form-message gm-quiz__form-message--error";
      messageEl.setAttribute("data-quiz-form-message", "true");
      form.prepend(messageEl);
    }

    messageEl.textContent = message;
  }

  updateVariantSelection(input) {
    const form = input.closest("form");
    if (!form) {
      return;
    }

    const variantInput = form.querySelector("[data-quiz-variant-id]");
    if (variantInput) {
      variantInput.value = input.value;
    }

    this.updatePlanOptionsForVariant(form, input.value);
  }

  updatePlanSelection(input) {
    const form = input.closest("form");
    if (!form) {
      return;
    }

    const selectedOption = input.selectedOptions ? input.selectedOptions[0] : null;
    const price = selectedOption ? selectedOption.dataset.price : "";
    if (price) {
      this.updateOfferPrice(price);
    }

    this.updateSellingPlan(form, input.value);
  }

  syncOfferForm() {
    const form = this.querySelector("[data-quiz-offer-form]");
    if (!form) {
      return;
    }

    const planInput = form.querySelector("[data-quiz-selling-plan]");
    if (!planInput) {
      return;
    }

    const selectedToggle = this.querySelector("[data-quiz-subscription]:checked");
    const isSubscribe = selectedToggle && selectedToggle.value === "subscribe";
    const planWrapper = form.querySelector("[data-quiz-plan-select-wrapper]");
    const planSelect = form.querySelector("[data-quiz-plan-select]");

    if (planWrapper) {
      planWrapper.hidden = !isSubscribe;
    }

    if (!isSubscribe) {
      planInput.value = "";
      return;
    }

    const planValue = (planSelect && planSelect.value) || (selectedToggle && selectedToggle.dataset.sellingPlan) || "";
    planInput.value = planValue;
  }

  updateSellingPlan(form, planId) {
    const planInput = form.querySelector("[data-quiz-selling-plan]");
    if (planInput) {
      planInput.value = planId || "";
    }
  }

  updatePlanOptionsForVariant(form, variantId) {
    if (!form || !this.planData) {
      this.syncOfferForm();
      return;
    }

    const plans = this.planData[variantId] || [];
    const planSelect = form.querySelector("[data-quiz-plan-select]");
    const planWrapper = form.querySelector("[data-quiz-plan-select-wrapper]");
    const subscribeOption = form.querySelector("[data-quiz-subscribe-option]");
    const subscribeInput = subscribeOption ? subscribeOption.querySelector("[data-quiz-subscription][value='subscribe']") : null;
    const oneTimeInput = form.querySelector("[data-quiz-subscription][value='one_time']");

    if (!plans.length) {
      if (subscribeOption) {
        subscribeOption.hidden = true;
      }
      if (subscribeInput && subscribeInput.checked && oneTimeInput) {
        oneTimeInput.checked = true;
        if (oneTimeInput.dataset.price) {
          this.updateOfferPrice(oneTimeInput.dataset.price);
        }
      }
      if (planWrapper) {
        planWrapper.hidden = true;
      }
      this.syncOfferForm();
      return;
    }

    if (subscribeOption) {
      subscribeOption.hidden = false;
    }

    if (planSelect) {
      planSelect.innerHTML = "";
      plans.forEach((plan, index) => {
        const option = document.createElement("option");
        option.value = plan.id;
        option.textContent = plan.name;
        if (plan.price) {
          option.dataset.price = plan.price;
        }
        if (index === 0) {
          option.selected = true;
        }
        planSelect.appendChild(option);
      });
    }

    if (subscribeInput) {
      subscribeInput.dataset.sellingPlan = plans[0] ? plans[0].id : "";
    }

    this.syncOfferForm();
  }

  collectAnswers(stepEl) {
    const key = stepEl.dataset.answerKey;
    if (!key) {
      return;
    }

    const inputs = Array.from(stepEl.querySelectorAll("[data-quiz-input]"));
    const checked = inputs.filter((input) => input.checked);

    if (stepEl.dataset.answerType === "multi") {
      this.answers[key] = checked.map((input) => input.value);
      this.answers[`${key}Labels`] = checked.map((input) => input.dataset.label || input.value);
    } else {
      const selected = checked.length ? checked[0] : null;
      this.answers[key] = selected ? selected.value : "";
      this.answers[`${key}Label`] = selected ? selected.dataset.label || selected.value : "";
    }
  }

  updateContinueState(stepEl) {
    const continueButton = stepEl.querySelector("[data-quiz-continue]");
    if (!continueButton) {
      return;
    }

    if (stepEl.dataset.required !== "true") {
      continueButton.disabled = false;
      return;
    }

    const inputs = Array.from(stepEl.querySelectorAll("[data-quiz-input]"));
    continueButton.disabled = !inputs.some((input) => input.checked);
  }

  goNext() {
    const stepEl = this.getCurrentStepEl();
    if (!stepEl) {
      return;
    }

    if (stepEl.dataset.required === "true") {
      const inputs = Array.from(stepEl.querySelectorAll("[data-quiz-input]"));
      if (!inputs.some((input) => input.checked)) {
        return;
      }
    }

    this.collectAnswers(stepEl);

    const nextStepId = this.getNextStepId(stepEl.dataset.quizStep);
    if (!nextStepId) {
      return;
    }

    this.history.push(stepEl.dataset.quizStep);
    this.showStep(nextStepId);
  }

  goBack() {
    const prevStepId = this.history.pop();
    if (!prevStepId) {
      return;
    }

    this.showStep(prevStepId);
  }

  showStep(stepId) {
    this.currentStepId = stepId;

    this.steps.forEach((step) => {
      const isActive = step.dataset.quizStep === stepId;
      step.classList.toggle("is-active", isActive);
      if (isActive) {
        step.removeAttribute("hidden");
      } else {
        step.setAttribute("hidden", "");
      }
    });

    const activeStep = this.getCurrentStepEl();
    if (activeStep) {
      const progress = activeStep.dataset.progress || "0";
      if (this.progressBar) {
        this.progressBar.style.width = `${progress}%`;
      }
      if (this.progressLabel) {
        this.progressLabel.textContent = `${progress}%`;
      }
      this.updateContinueState(activeStep);
      this.setHeaderHeight();

      const backButton = activeStep.querySelector("[data-quiz-back]");
      if (backButton) {
        backButton.disabled = this.history.length === 0;
      }
    }
  }

  getCurrentStepEl() {
    return this.steps.find((step) => step.dataset.quizStep === this.currentStepId) || null;
  }

  getNextStepId(currentId) {
    switch (currentId) {
      case "1":
        return "2";
      case "2":
        return "3";
      case "3":
        return "4";
      case "4":
        return "5";
      case "5":
        return "6";
      case "6":
        return "7";
      case "7":
        return this.isProbioticYes() ? "8a" : "8b";
      case "8a":
      case "8b":
        return "9";
      case "9":
        return this.shouldShowGummyTransition() ? "9b" : "10";
      case "9b":
        return "10";
      case "10":
        return "11";
      case "11":
        return "12";
      case "12":
        return "13";
      case "13":
        return null;
      default:
        return null;
    }
  }

  isProbioticYes() {
    const value = this.answers.probiotic || "";
    if (!this.probioticYesValues.length) {
      return false;
    }

    return this.probioticYesValues.includes(value);
  }

  shouldShowGummyTransition() {
    const value = this.answers.format || "";
    if (!value) {
      return false;
    }

    if (!this.gummyValue) {
      return false;
    }

    return value !== this.gummyValue;
  }

  updateOfferPrice(price) {
    const priceEl = this.querySelector("[data-quiz-price-current]");
    if (priceEl && price) {
      priceEl.textContent = price;
    }
  }

  updateOfferBenefits() {
    const listEl = this.querySelector("[data-quiz-symptom-list]");
    if (!listEl) {
      return;
    }

    const selected = this.getSelectedSymptomLabels();
    const stored = this.answers.symptomsLabels || [];
    const items = selected.length ? selected : stored.length ? stored : this.fallbackBenefits;
    const limit = Number.isFinite(this.symptomLimit) ? this.symptomLimit : 3;
    const template = this.querySelector("[data-quiz-benefit-template]");

    if (!items.length) {
      return;
    }

    listEl.innerHTML = "";

    items.slice(0, limit).forEach((item) => {
      if (template && template.content.firstElementChild) {
        const li = template.content.firstElementChild.cloneNode(true);
        const textEl = li.querySelector("[data-quiz-benefit-text]");
        if (textEl) {
          textEl.textContent = item;
        }
        listEl.appendChild(li);
      } else {
        const li = document.createElement("li");
        li.className = "gm-quiz__benefit-item";
        li.textContent = item;
        listEl.appendChild(li);
      }
    });
  }

  getSelectedSymptomLabels() {
    const inputs = Array.from(this.querySelectorAll("[data-symptom-input]"));
    return inputs
      .filter((input) => input.checked)
      .map((input) => input.dataset.label || input.value);
  }

  sendKlaviyo(email) {
    if (!this.klaviyoKey || !email) {
      return Promise.resolve();
    }

    const answers = this.buildKlaviyoAnswers();
    const profileProperties = {
      $email: email,
      quiz_name: "Gut Master Quiz",
    };

    if (this.klaviyoTag) {
      profileProperties.quiz_tag = this.klaviyoTag;
      profileProperties.tags = [this.klaviyoTag];
    }

    const eventProperties = {
      quiz_name: "Gut Master Quiz",
      ...answers,
    };

    if (this.klaviyoTag) {
      eventProperties.quiz_tag = this.klaviyoTag;
    }

    if (window._learnq && Array.isArray(window._learnq)) {
      window._learnq.push(["identify", profileProperties]);
      window._learnq.push(["track", this.klaviyoEventName, eventProperties]);
      return Promise.resolve();
    }

    const identifyPayload = {
      token: this.klaviyoKey,
      properties: profileProperties,
    };

    const trackPayload = {
      token: this.klaviyoKey,
      event: this.klaviyoEventName,
      customer_properties: {
        $email: email,
      },
      properties: eventProperties,
    };

    const identifyPromise = this.sendKlaviyoRequest("https://a.klaviyo.com/api/identify", identifyPayload);
    const trackPromise = this.sendKlaviyoRequest("https://a.klaviyo.com/api/track", trackPayload);

    return Promise.allSettled([identifyPromise, trackPromise]);
  }

  sendKlaviyoRequest(url, payload) {
    const encoded = this.encodeKlaviyoPayload(payload);
    if (!encoded) {
      return Promise.resolve();
    }

    const body = new URLSearchParams({ data: encoded });

    if (navigator.sendBeacon) {
      try {
        navigator.sendBeacon(url, body);
        return Promise.resolve();
      } catch (error) {
        // Fallback to fetch below.
      }
    }

    return fetch(url, {
      method: "POST",
      mode: "no-cors",
      body,
      keepalive: true,
    });
  }

  encodeKlaviyoPayload(payload) {
    try {
      const json = JSON.stringify(payload);
      return btoa(unescape(encodeURIComponent(json)));
    } catch (error) {
      return null;
    }
  }

  buildKlaviyoAnswers() {
    const answers = {};

    if (this.answers.timeLabel) {
      answers.time_dealing_with_issues = this.answers.timeLabel;
    } else if (this.answers.time) {
      answers.time_dealing_with_issues = this.answers.time;
    }

    if (this.answers.symptomsLabels && this.answers.symptomsLabels.length) {
      answers.symptoms = this.answers.symptomsLabels;
    }

    if (this.answers.triedLabels && this.answers.triedLabels.length) {
      answers.tried_solutions = this.answers.triedLabels;
    }

    if (this.answers.probioticLabel) {
      answers.current_probiotic_use = this.answers.probioticLabel;
    } else if (this.answers.probiotic) {
      answers.current_probiotic_use = this.answers.probiotic;
    }

    if (this.answers.formatLabel) {
      answers.supplement_format = this.answers.formatLabel;
    } else if (this.answers.format) {
      answers.supplement_format = this.answers.format;
    }

    return answers;
  }
}

if (!window.customElements.get("gut-master-quiz")) {
  window.customElements.define("gut-master-quiz", GutMasterQuiz);
}
